import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@sea-snag/ui';
import { useMemo } from 'react';

import type { CalendarWeek } from '../lib/api-client';
import { formatWeekRange, getPositionColor } from './comp-set-shared';

const CELL_W = 14;
const CELL_H = 24;
const GAP = 2;
const TOTAL = CELL_W + GAP;
const TOP_PADDING = 16;
const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

interface MonthLabel {
  label: string;
  x: number;
}

function getMonthLabels(weeks: CalendarWeek[]): MonthLabel[] {
  const labels: MonthLabel[] = [];
  let lastMonth = -1;

  for (let i = 0; i < weeks.length; i++) {
    const month = new Date(weeks[i]!.weekStart).getMonth();
    if (month !== lastMonth) {
      lastMonth = month;
      labels.push({ label: MONTH_NAMES[month]!, x: i * TOTAL });
    }
  }

  return labels;
}

interface WeeklyPositionStripProps {
  data: CalendarWeek[];
  onWeekClick?: (weekIndex: number) => void;
  selectedIndex?: number;
}

export function WeeklyPositionStrip({
  data,
  onWeekClick,
  selectedIndex,
}: WeeklyPositionStripProps) {
  const monthLabels = useMemo(() => getMonthLabels(data), [data]);

  const svgWidth = data.length * TOTAL;
  const svgHeight = TOP_PADDING + CELL_H + 4;

  if (data.length === 0) return null;

  return (
    <TooltipProvider delayDuration={100}>
      <div>
        <svg
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          className="w-full select-none"
          preserveAspectRatio="xMinYMid meet"
          role="img"
          aria-label="Year-at-a-glance position strip"
        >
          {/* Month labels */}
          {monthLabels.map((m) => (
            <text
              key={`${m.label}-${m.x}`}
              x={m.x}
              y={11}
              className="fill-muted-foreground text-[9px]"
            >
              {m.label}
            </text>
          ))}

          {/* Position cells */}
          {data.map((week, i) => {
            const x = i * TOTAL;
            const y = TOP_PADDING;
            const fill = week.position ? getPositionColor(week.position) : '#e5e7eb';
            const range = formatWeekRange(week.weekStart, week.weekEnd);
            const pctText =
              week.positionPct != null
                ? `${week.positionPct > 0 ? '+' : ''}${week.positionPct.toFixed(1)}%`
                : 'No data';

            return (
              <Tooltip key={week.weekStart}>
                <TooltipTrigger asChild>
                  <rect
                    x={x}
                    y={y}
                    width={CELL_W}
                    height={CELL_H}
                    rx={3}
                    fill={fill}
                    opacity={i === selectedIndex ? 1 : week.position ? 0.85 : 0.3}
                    className="cursor-pointer transition-opacity hover:opacity-100"
                    onClick={() => onWeekClick?.(i)}
                    stroke={i === selectedIndex ? 'hsl(var(--ring))' : 'none'}
                    strokeWidth={i === selectedIndex ? 2 : 0}
                  />
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  <p className="font-medium">{range}</p>
                  <p className="text-muted-foreground">
                    {week.position ? `${week.position} (${pctText})` : 'No position data'}
                  </p>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </svg>
      </div>
    </TooltipProvider>
  );
}

export function PositionStripLegend() {
  const items: { label: string; color: string }[] = [
    { label: 'Competitive', color: '#22c55e' },
    { label: 'Underpriced', color: '#f59e0b' },
    { label: 'Overpriced', color: '#ef4444' },
    { label: 'No data', color: '#e5e7eb' },
  ];

  return (
    <div className="flex items-center gap-4 text-xs text-muted-foreground">
      {items.map((item) => (
        <div key={item.label} className="flex items-center gap-1.5">
          <div className="size-2.5 rounded-sm" style={{ backgroundColor: item.color }} />
          <span>{item.label}</span>
        </div>
      ))}
    </div>
  );
}
